import * as React from "react"
import { Head, Link, router, usePage } from "@inertiajs/react"
import { AdminShell } from "@/components/AdminShell"
import { PageHeader } from "@/components/PageHeader"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { InlineField } from "@/components/ui/inline-edit"
import { useInlineRecord } from "@/hooks/use-inline-record"
import type { PageProps } from "@/types/inertia"

type MenuCondition = {
  id: number
  label: string
  nav_label_en: string
  kind: string
  status: string
  position: number
  edit_href: string
}

type Category = {
  id: number
  key: string
  label_en: string
  label_es: string
  position: number
  conditions: MenuCondition[]
}

type Props = PageProps & { categories: Category[] }

const KIND_LABELS: Record<string, string> = {
  page: "Full page",
  listing: "Listed only",
  link: "Link",
}

const DESCRIPTION =
  "The groups behind Gisela's “What I help with” menu, and every condition in them."

function CategoryCard({ category }: { category: Category }) {
  const inUse = category.conditions.length > 0

  const fields = useInlineRecord({
    record: {
      label_en: category.label_en,
      label_es: category.label_es,
      position: String(category.position),
    },
    url: `/admin/website/condition-categories/${category.id}`,
    param: "condition_category",
  })
  const v = fields.values
  const labelEnState = fields.state("label_en")
  const labelEsState = fields.state("label_es")
  const positionState = fields.state("position", { serialize: (value) => Number(value) })

  function remove() {
    if (!window.confirm(`Delete the “${category.label_en}” group? This cannot be undone.`)) return
    router.delete(`/admin/website/condition-categories/${category.id}`)
  }

  return (
    <div className="rounded-lg border border-hairline bg-surface p-5">
      <div className="grid gap-4 sm:grid-cols-[1fr_1fr_6rem]">
        <InlineField
          label="Group name (English)"
          htmlFor={`label_en_${category.id}`}
          state={labelEnState}
        >
          <Input
            id={`label_en_${category.id}`}
            value={v.label_en}
            onChange={(e) => fields.set("label_en", e.target.value)}
            onKeyDown={labelEnState.keys}
          />
        </InlineField>
        <InlineField
          label="Group name (Spanish)"
          htmlFor={`label_es_${category.id}`}
          state={labelEsState}
        >
          <Input
            id={`label_es_${category.id}`}
            value={v.label_es}
            onChange={(e) => fields.set("label_es", e.target.value)}
            onKeyDown={labelEsState.keys}
          />
        </InlineField>
        <InlineField label="Order" htmlFor={`position_${category.id}`} state={positionState}>
          <Input
            id={`position_${category.id}`}
            type="number"
            min={0}
            value={v.position}
            onChange={(e) => fields.set("position", e.target.value)}
            onKeyDown={positionState.keys}
          />
        </InlineField>
      </div>

      {category.conditions.length > 0 ? (
        <ul className="mt-5 m-0 list-none space-y-0 p-0 border-t border-hairline">
          {category.conditions.map((condition) => (
            <li
              key={condition.id}
              className="flex flex-wrap items-center gap-x-3 gap-y-1 border-b border-hairline py-2.5"
            >
              <span className="text-sm text-ink-display">{condition.nav_label_en}</span>
              <Badge tone="muted">{KIND_LABELS[condition.kind] ?? condition.kind}</Badge>
              {condition.status === "draft" && <Badge tone="signal">Draft</Badge>}
              <span className="text-xs text-ink-muted">#{condition.position}</span>
              <Link href={condition.edit_href} className="ml-auto text-sm">
                Settings →
              </Link>
            </li>
          ))}
        </ul>
      ) : (
        <p className="mt-5 border-t border-hairline pt-4 text-sm text-ink-muted">
          Nothing in this group yet — it stays out of the menu until something is added to it.
        </p>
      )}

      <div className="mt-4 flex flex-wrap items-center gap-3">
        {inUse && (
          <span className="text-xs text-ink-muted">
            Move its conditions to another group before deleting it.
          </span>
        )}
        <Button
          type="button"
          size="sm"
          variant="ghost"
          className="ml-auto text-danger-display"
          onClick={remove}
          disabled={inUse}
        >
          Delete group
        </Button>
      </div>
    </div>
  )
}

function AddCategory() {
  const [labelEn, setLabelEn] = React.useState("")
  const [labelEs, setLabelEs] = React.useState("")
  const [saving, setSaving] = React.useState(false)

  function submit(e: React.FormEvent) {
    e.preventDefault()
    setSaving(true)
    router.post(
      "/admin/website/condition-categories",
      { condition_category: { label_en: labelEn, label_es: labelEs } },
      {
        onSuccess: () => {
          setLabelEn("")
          setLabelEs("")
        },
        onFinish: () => setSaving(false),
      },
    )
  }

  return (
    <form onSubmit={submit} className="rounded-lg border border-dashed border-hairline p-5">
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-1.5">
          <label htmlFor="new_label_en">New group name (English)</label>
          <Input
            id="new_label_en"
            value={labelEn}
            onChange={(e) => setLabelEn(e.target.value)}
            required
            placeholder="e.g. Work & performance"
          />
        </div>
        <div className="space-y-1.5">
          <label htmlFor="new_label_es">New group name (Spanish)</label>
          <Input
            id="new_label_es"
            value={labelEs}
            onChange={(e) => setLabelEs(e.target.value)}
            required
            placeholder="e.g. Trabajo y rendimiento"
          />
        </div>
      </div>
      <div className="mt-4">
        <Button type="submit" variant="secondary" size="sm" disabled={saving}>
          Add group
        </Button>
      </div>
    </form>
  )
}

// The Condition menu screen. It's also the only place listed-only and link
// entries can be reached from — they have no page, so no node in the Website
// Pages tree.
export default function ConditionCategoriesIndex() {
  const { props } = usePage<Props>()
  const flash = props.flash

  return (
    <AdminShell>
      <Head title="Condition Menu">
        <meta name="description" content={DESCRIPTION} />
        <meta property="og:title" content="Condition Menu" />
        <meta property="og:description" content={DESCRIPTION} />
      </Head>

      <PageHeader
        title="Condition menu"
        description={DESCRIPTION}
        actions={
          <Button variant="secondary" asChild>
            <Link href="/admin/website/conditions/new">+ New condition</Link>
          </Button>
        }
      />

      {flash?.notice && <p className="mt-4 text-sm text-accent">{flash.notice}</p>}
      {flash?.alert && <p className="mt-4 text-sm text-danger-display">{flash.alert}</p>}

      <p className="mt-6 max-w-3xl text-sm text-ink-muted">
        The first group listed here shows straight inside the “What I help with” menu. Every group
        after it becomes its own row in that menu, opening a side list — so put the conditions people
        search for most in the first group.
      </p>

      <div className="mt-6 flex max-w-3xl flex-col gap-4">
        {props.categories.map((category) => (
          <CategoryCard key={category.id} category={category} />
        ))}
        <AddCategory />
      </div>
    </AdminShell>
  )
}
